/** @jsxImportSource @emotion/react */
import React, { memo, useMemo } from 'react'
import { useAppEntryTelemetry } from '@signal/core/instrumentation/hooks'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import renderIf from '@signal/utils/renderIf'
import { AnyReactComponentType } from '@signal/utils/types'
import HeaderNew from '../HeaderNew'
import MultiAppMenuPanel from '../MultiAppMenuPanel'
import UnauthorizedErrorBoundary from '../UnauthorizedErrorBoundary'
import { ChildWrapper, ScrollWrapper, Wrapper } from './styles'
import { CommonPagePropsType } from './types'

export const CommonPage: React.FC<CommonPagePropsType> = ({
  children,
  // useful for taking screen shots without using real dealer names
  accountNameOverride,
  // the minimum width the app needs to render properly
  appMinWidth,
  // the array of query parameters supported by this application
  appSupportedQP,
  // if you need extra menu items beyond the ones which come from the menu service, add them here
  extraMenuItems,
  // if true, hide all chrome and just render the page component
  hideChrome,
  // if true, hide the footer
  hideFooter,
  // if true, hide the main navigation menu on the left
  hideMenu,
  // if true, then hide RightMenu
  hideRightHandMenu,
  // a location object, such as from window or react-router
  location,
  // if true, then this is being used by a baseline app
  fromBaseline = true,
  // your app needs to handle account change when recent accounts or group/subgroup accounts are selected
  onAccountChange,
  // your app needs to handle logout from the "sign out" button in the header
  onLogout,
  // your app needs to handle navigation requests from the multi-app menu.  This function is curried
  onNavigate,
  // extra content to put in the header
  ExtraHeader,
  // if you have additional content to display in the right-hand nav menu, supply it here
  UserMenuNavLinks,
  ...rest
}) => {
  useAppEntryTelemetry()

  // cast loosens prop typing the same way the previous multi-header switch did,
  // since CommonPage's handler signatures are wider than HeaderNew's props
  const ActiveHeader = HeaderNew as AnyReactComponentType
  const ExtraHeaderRender = ExtraHeader as AnyReactComponentType

  const Left = useMemo(() => {
    if (!location || !onNavigate) return undefined
    return () => (
      <MultiAppMenuPanel
        appSupportedQP={appSupportedQP}
        extraMenuItems={extraMenuItems}
        location={location}
        onNavigate={onNavigate}
      />
    )
  }, [location, onNavigate, appSupportedQP, extraMenuItems])

  return (
    <Wrapper
      adjustPageContainerForMenu={!(hideMenu || hideChrome)}
      constrainWidth={false}
      disableGutter
      left={hideChrome || hideMenu ? undefined : Left}
    >
      {renderIf(!hideChrome, () => (
        <ActiveHeader
          hideMultiAppMenu
          hideLogo
          hideFooter={hideFooter}
          accountNameOverride={accountNameOverride}
          location={location}
          onAccountChange={onAccountChange}
          onLogout={onLogout}
          onNavigate={onNavigate}
          UserMenuNavLinks={UserMenuNavLinks}
          hideMenu={hideMenu}
          hideRightHandMenu={hideRightHandMenu}
          {...rest}
        >
          {renderIf(Boolean(ExtraHeader), () => (
            <ExtraHeaderRender />
          ))}
        </ActiveHeader>
      ))}
      {renderIf(Boolean(children), () =>
        renderIf(
          Boolean(appMinWidth),
          () => (
            <ScrollWrapper
              appMinWidth={appMinWidth}
              hideChrome={hideChrome}
              hideFooter={hideFooter}
              fromBaseline={fromBaseline}
            >
              <ChildWrapper appMinWidth={appMinWidth}>
                <UnauthorizedErrorBoundary>
                  {children}
                </UnauthorizedErrorBoundary>
              </ChildWrapper>
            </ScrollWrapper>
          ),
          () => (
            <UnauthorizedErrorBoundary>{children}</UnauthorizedErrorBoundary>
          )
        )
      )}
    </Wrapper>
  )
}

export default memo(withCommonSnapshotsOnly(CommonPage))
